import React from 'react';
import { useScheduledCallVideo } from '../contexts/ScheduledCallVideoContext';
import { ScheduledCallParticipant } from '../contexts/ScheduledCallVideoContext';

export type { ScheduledCallParticipant };

export interface UseScheduledCallTwilioVideoOptions {
   roomName: string;
   identity: string;
   onRoomJoined?: (room: any) => void;
   onRoomLeft?: () => void;
   onParticipantConnected?: (participant: any) => void;
   onParticipantDisconnected?: (participant: any) => void;
   onError?: (error: Error) => void;
   role: 'host' | 'participant';
   connectionKey?: string;
}

export interface UseScheduledCallTwilioVideoReturn {
   // Connection state
   room: any;
   isConnecting: boolean;
   isConnected: boolean;
   error: string | null;

   // Participants
   participants: ScheduledCallParticipant[];
   localParticipant: ScheduledCallParticipant | null;
   dominantSpeaker: ScheduledCallParticipant | null;
   currentScreenSharer: ScheduledCallParticipant | null;

   // Media controls
   isVideoEnabled: boolean;
   isAudioEnabled: boolean;
   isScreenSharing: boolean;

   // View mode states
   isPopupMode: boolean;
   isMinimized: boolean;

   // Actions
   connectToRoom: () => Promise<void>;
   disconnectFromRoom: () => void;
   toggleVideo: () => Promise<void>;
   toggleAudio: () => Promise<void>;
   toggleScreenShare: () => Promise<void>;
   forceReattachThumbnails: () => void;

   // Refs for video containers
   localVideoRef: React.RefObject<HTMLVideoElement>;
   remoteVideoContainerRef: React.RefObject<HTMLDivElement>;

   // Screen share track
   screenShareTrack: any;
}

export const useScheduledCallTwilioVideo = (
   options: UseScheduledCallTwilioVideoOptions
): UseScheduledCallTwilioVideoReturn => {
   const { onError } = options;

   // Get everything from the scheduled call context
   const {
      videoCallState,
      toggleVideo,
      toggleAudio,
      toggleScreenShare,
      localVideoRef,
      remoteVideoContainerRef,
      endScheduledCall,
      leaveScheduledCall,
      forceReattachThumbnails,
   } = useScheduledCallVideo();

   // If there's an error, call the error callback
   if (videoCallState.error && onError) {
      onError(new Error(videoCallState.error));
   }

   return {
      // Connection state
      room: videoCallState.room,
      isConnecting: videoCallState.isConnecting,
      isConnected: videoCallState.isConnected,
      error: videoCallState.error,

      // Participants
      participants: videoCallState.participants,
      localParticipant: videoCallState.localParticipant,
      dominantSpeaker: videoCallState.dominantSpeaker,
      currentScreenSharer: videoCallState.currentScreenSharer,

      // Media controls
      isVideoEnabled: videoCallState.isVideoEnabled,
      isAudioEnabled: videoCallState.isAudioEnabled,
      isScreenSharing: videoCallState.isScreenSharing,

      // View mode states
      isPopupMode: videoCallState.isPopupMode,
      isMinimized: videoCallState.isMinimized,

      // Actions
      connectToRoom: () => Promise.resolve(), // No-op since connection is managed globally
      disconnectFromRoom: leaveScheduledCall,
      toggleVideo,
      toggleAudio,
      toggleScreenShare,
      forceReattachThumbnails,

      // Refs
      localVideoRef,
      remoteVideoContainerRef,

      // Screen share track
      screenShareTrack: videoCallState.screenShareTrack,
   };
};
